import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import './FundScreenerTable.css';
import '../Common/typography.css';
import { ScreenerTableColumnType } from '../../types/screener.type';
import { FundScreenerDataType } from '../../types/fundScreener.type';
import { getCssVariable } from '../../utils/utils';

interface FundScreenerTableProps {
  columns: ScreenerTableColumnType[];
  data: FundScreenerDataType[];
  isSmallScreen?: boolean;
}

// Index/Flag/Name are fixed-width; everything after shares the remaining
// space. Widths must be set via `sx` on the header row (tableLayout: fixed).
const INDEX_COLUMN_WIDTH = 30;
// Must match `.fund-screener-table-flag`'s rendered width exactly — the
// sticky Name column's `left` offset is computed from these constants.
const FLAG_COLUMN_WIDTH = 32;
const NAME_COLUMN_WIDTH = 320;
// Mobile drops the index column and shrinks Name to make room.
// Keep in sync with `.fund-screener-table-name-mobile` in the CSS file.
const NAME_COLUMN_WIDTH_MOBILE = 160;
const MIN_EXPANDABLE_COLUMN_WIDTH = 140;
const MIN_NAV_COLUMN_WIDTH_MOBILE = 100;
const MIN_TNA_COLUMN_WIDTH = 170;
const MIN_TNA_COLUMN_WIDTH_MOBILE = 130;
// Company names run long (e.g. "AllianceBernstein Inv Management Australia
// Ltd") — wide enough to show most in full, the rest truncate + tooltip.
const MIN_FUND_COMPANY_COLUMN_WIDTH = 260;
const isFixedWidthColumn = (id: string) =>
  id === 'index' || id === 'flag' || id === 'name';
const getFixedColumnWidth = (id: string, isSmallScreen?: boolean) => {
  if (id === 'index') return INDEX_COLUMN_WIDTH;
  if (id === 'flag') return FLAG_COLUMN_WIDTH;
  return isSmallScreen ? NAME_COLUMN_WIDTH_MOBILE : NAME_COLUMN_WIDTH;
};
const getExpandableColumnWidth = (id: string, isSmallScreen?: boolean) => {
  if (id === 'tna')
    return isSmallScreen ? MIN_TNA_COLUMN_WIDTH_MOBILE : MIN_TNA_COLUMN_WIDTH;
  if (id === 'fundCompany') return MIN_FUND_COMPANY_COLUMN_WIDTH;
  if (id === 'nav' && isSmallScreen) return MIN_NAV_COLUMN_WIDTH_MOBILE;
  return MIN_EXPANDABLE_COLUMN_WIDTH;
};

const FundScreenerTable = ({
  columns,
  data,
  isSmallScreen,
}: FundScreenerTableProps) => {
  // Index is excluded on mobile, so derive its width from what's present.
  const hasIndexColumn = columns.some((column) => column.id === 'index');
  const fixedColumnsWidth =
    (hasIndexColumn ? INDEX_COLUMN_WIDTH : 0) +
    FLAG_COLUMN_WIDTH +
    (isSmallScreen ? NAME_COLUMN_WIDTH_MOBILE : NAME_COLUMN_WIDTH);

  const minTableWidth =
    fixedColumnsWidth +
    columns.reduce(
      (sum, column) =>
        isFixedWidthColumn(column.id)
          ? sum
          : sum + getExpandableColumnWidth(column.id, isSmallScreen),
      0,
    );

  return (
    <TableContainer
      className="fund-screener-table-container"
      component={Paper}
      sx={{ position: 'relative', flex: 1, overflow: 'auto' }}
      elevation={0}
    >
      <Table
        stickyHeader
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          // table-layout: fixed lives in the CSS file. Grows past 100% below
          // the column minimum, letting overflow scroll instead of squeezing.
          width: `max(100%, ${minTableWidth}px)`,
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                className={column.headerClassName}
                sx={{
                  padding: isFixedWidthColumn(column.id)
                    ? '4px 0px'
                    : '4px 16px',
                  // Re-asserts `.header-6` typography — MUI's own TableCell
                  // class otherwise beats a plain CSS class.
                  fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: getCssVariable('--text-subheader'),
                  borderBottom: 'none',
                  boxShadow: `inset 0 -1px 0 0 ${getCssVariable('--text-subheader')}`,
                  textAlign: column.id === 'index' ? 'center' : undefined,
                  width: isFixedWidthColumn(column.id)
                    ? getFixedColumnWidth(column.id, isSmallScreen)
                    : getExpandableColumnWidth(column.id, isSmallScreen),
                }}
              >
                {column.renderHeader
                  ? column.renderHeader(column.id)
                  : column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={row.ric || index}
              sx={{
                height: '56px',
                '&:nth-of-type(2n)': {
                  backgroundColor: getCssVariable('--row-dark'),
                  '& td:nth-of-type(1), & td:nth-of-type(2), & td:nth-of-type(3)':
                    {
                      backgroundColor: getCssVariable('--row-dark'),
                    },
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  className={column.cellClassName}
                  sx={{
                    // Set here, not via CSS classes — sx wins the
                    // specificity fight against this file's plain CSS, so a
                    // CSS-only padding silently gets zeroed out.
                    padding: isFixedWidthColumn(column.id) ? '0px' : '0 16px',
                    color: getCssVariable('--text-primary'),
                    verticalAlign: 'middle',
                    borderBottom: 'none',
                    cursor: column.id === 'code' ? 'pointer' : 'default',
                  }}
                >
                  {column.renderCell
                    ? column.renderCell(row, index)
                    : (row as any)[column.accessorKey]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FundScreenerTable;
